import { Badge } from '@/components/ui/badge';

type StatusVariant = 'success' | 'danger' | 'warning' | 'info' | 'neutral';

type WorkspaceStatusSectionProps = {
  label?: string;
  statusLabel: string;
  variant?: StatusVariant;
};

export function getActiveStatusInfo(active: boolean | undefined | null) {
  return active
    ? { statusLabel: 'Active', variant: 'success' as const }
    : { statusLabel: 'Deactivated', variant: 'danger' as const };
}

export function getRecordStatusInfo(state: unknown) {
  const normalizedState = String(state || '').toUpperCase();

  if (normalizedState === 'ACTIVE') return { statusLabel: 'Active', variant: 'success' as const };
  if (normalizedState === 'SUCCEEDED' || normalizedState === 'SUCCESS') {
    return { statusLabel: 'Succeeded', variant: 'success' as const };
  }
  if (normalizedState === 'ERROR' || normalizedState === 'FAILED') {
    return { statusLabel: 'Failed', variant: 'danger' as const };
  }
  if (normalizedState.includes('DISPUTED')) {
    return { statusLabel: 'Disputed', variant: 'danger' as const };
  }
  if (normalizedState === 'FINISHED') {
    return { statusLabel: 'Finished', variant: 'info' as const };
  }
  if (normalizedState === 'DEACTIVATED') {
    return { statusLabel: 'Deactivated', variant: 'danger' as const };
  }
  if (normalizedState === 'PENDING') return { statusLabel: 'Pending', variant: 'warning' as const };
  if (normalizedState === 'PAUSED') return { statusLabel: 'Paused', variant: 'warning' as const };
  if (normalizedState === 'CANCELLED' || normalizedState === 'CANCELED') {
    return { statusLabel: 'Canceled', variant: 'warning' as const };
  }
  if (normalizedState === 'PARTIAL_REFUND') {
    return { statusLabel: 'Partial Refund', variant: 'neutral' as const };
  }
  if (normalizedState === 'FULL_REFUND' || normalizedState === 'REFUNDED') {
    return { statusLabel: 'Full Refund', variant: 'neutral' as const };
  }

  if (!normalizedState) return { statusLabel: 'Unknown', variant: 'neutral' as const };

  return {
    statusLabel: normalizedState
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase()),
    variant: 'neutral' as const,
  };
}

export function getSubscriptionStatusInfo(subscription: {
  subscriptionStatus?: string;
  dateLastCharged?: string | null;
  scheduled?: boolean;
} | null | undefined) {
  const state = String(subscription?.subscriptionStatus || '').toUpperCase();

  if (state === 'ACTIVE' && !subscription?.dateLastCharged && subscription?.scheduled) {
    return { statusLabel: 'Scheduled', variant: 'neutral' as const };
  }

  return getRecordStatusInfo(state);
}

export default function WorkspaceStatusSection({
  label = 'Status',
  statusLabel,
  variant = 'neutral',
}: WorkspaceStatusSectionProps) {
  return (
    <div className="flex items-center justify-between rounded-md border border-edge bg-white px-4 py-3 text-sm text-ink-muted">
      <span className="font-medium">{label}</span>
      <Badge className="h-6 rounded-md text-xs font-medium" variant={variant}>
        {statusLabel}
      </Badge>
    </div>
  );
}
